import {
  addToDate,
  isDateInFuture,
  useAssetContext,
  useAuthenticatedUser,
  useHttpClient,
  useLocalStorage,
  useNetworkStatus,
} from '@airship-platform/airship-patterns';
import { type OrganizationPresentationContentResponse } from '@airship-platform/proposal-presentations-feature/schema';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useOfflineProviderInit } from '../../rxdb/useOfflineProviderInit';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';
import RxDBSetupNotificationAlert from '../../rxdb/utils/RxDBSetupNotificationAlert';
import { orgContentAssetUrls } from './orgContentAssetUrls';

const OFFLINE_KEY = 'offline-sync-org-content';
const OFFLINE_SYNC_HOURS = 1;
const syncedInLastNHours = (offlineSync: string, hours = OFFLINE_SYNC_HOURS) =>
  isDateInFuture(addToDate(hours, 'h', offlineSync));

// Best-effort: pull the assets through the service worker's routes while the
// image signatures are still valid, so a later offline render finds them
// cached. Never allowed to fail the sync it rides along with.
const warmAssetCaches = (urls: string[]) =>
  Promise.allSettled(Array.from(new Set(urls)).map((url) => fetch(url)));

interface OrgContentContextProps {
  presentationContent: OrganizationPresentationContentResponse | null;
  syncOrgContent: () => Promise<void>;
  orgContentSync: { error?: string | null; lastSyncTime?: string };
}

const OrgContentContext = createContext<OrgContentContextProps | undefined>(
  undefined
);

// Scheduled pull of the org-level presentation content (financing rates,
// terms, FAQs, core values, promos) into RxDB, republished as state for
// synchronous reads. Deliberately not opt-in gated: the payload is tiny and
// non-opted-in users should still get as complete an offline presentation as
// possible. State is keyed by the logged-in user's organization rather than
// "whatever document exists" so a stale other-org document can never leak
// into a presentation, even if the wipe-at-logout behavior changes.
export const OfflineOrgContentProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const data = useOfflineDbData();
  const httpClient = useHttpClient();
  const { user } = useAuthenticatedUser();
  const { getAssetUrl } = useAssetContext();
  const isOnline = useNetworkStatus();
  const [lastSyncTime, setLastSyncTime] = useLocalStorage(OFFLINE_KEY);
  const [error, setError] = useState<string | null>(null);
  const [presentationContent, setPresentationContent] =
    useState<OrganizationPresentationContentResponse | null>(null);

  const fetchAndCache = useCallback(() => {
    const setSyncedNow = () =>
      Promise.resolve(setLastSyncTime(addToDate(0, 'h').toISOString()));

    return httpClient
      .get<OrganizationPresentationContentResponse>(
        '/proposal-presentations/organization-content'
      )
      .then((content) =>
        data
          .bulkUpsert('organization_presentation_content', [content])
          .then(setSyncedNow)
          .then(() => {
            void warmAssetCaches(orgContentAssetUrls(content, getAssetUrl));
          })
      );
  }, [data, httpClient, setLastSyncTime, getAssetUrl]);

  const populateContentState = useCallback(async () => {
    if (!user?.organizationId) {
      setPresentationContent(null);
      return;
    }

    return data
      .findOne('organization_presentation_content', user.organizationId)
      .then((doc) => setPresentationContent(doc ?? null))
      .catch((error) => setError(`${error}`));
  }, [data, user?.organizationId]);

  const syncOrgContent = useCallback(async () => {
    setError(null);
    if (isOnline()) {
      await fetchAndCache().catch((error) => setError(`${error}`));
    }
    await populateContentState();
  }, [isOnline, fetchAndCache, populateContentState]);

  const init = useCallback(async () => {
    await populateContentState();

    if (!lastSyncTime || !syncedInLastNHours(lastSyncTime)) {
      syncOrgContent();
    }
  }, [lastSyncTime, syncOrgContent, populateContentState]);

  const { initError } = useOfflineProviderInit(init);

  if (initError) {
    return (
      <RxDBSetupNotificationAlert
        error={initError}
        entityName="organization content"
      />
    );
  }

  return (
    <OrgContentContext.Provider
      value={{
        presentationContent,
        syncOrgContent,
        orgContentSync: { error, lastSyncTime },
      }}
    >
      {children}
    </OrgContentContext.Provider>
  );
};

export const useOrgPresentationContent = () => {
  const context = useContext(OrgContentContext);
  if (context === undefined) {
    throw new Error(
      'useOrgPresentationContent must be used within an OfflineOrgContentProvider'
    );
  }
  return context;
};
